'use client';

import React, { useRef, useState } from 'react';
import { Grid, Box, Typography, Paper, Fade, Button, CircularProgress } from '@mui/material';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { useEstimator } from '../../context/EstimatorContext';
import ProjectBudgetSummary from './ProjectBudgetSummary';
import LogoImage from '../../assets/images/wwm-logo-2.png';
import ProjectMetricsSummary from './ProjectMetricsSummary';
import CostAssumptionsSummary from './CostAssumptionsSummary';
import ProjectQuantitiesSummary from './ProjectQuantitiesSummary';
import ProjectBudgetPieChart from './ProjectBudgetPieChart';
import PdfExportDocument from './PdfExportDocument';

const Summary = () => {
  const { formData, budgetSummaryRows } = useEstimator();

  const chartRef = useRef(null);
  const [chartImage, setChartImage] = useState(null);
  const [generating, setGenerating] = useState(false);

  const prepareChartImage = async () => {
    console.log('formData:', formData);
    console.log('budgetSummaryRows:', budgetSummaryRows);

    if (!chartRef.current?.getImageDataUrl || !budgetSummaryRows || budgetSummaryRows.length === 0) {
      console.warn('Chart data not ready');
      return;
    }

    setGenerating(true);
    const image = await chartRef.current.getImageDataUrl();
    if (image) {
      setChartImage(image);
    }
    setGenerating(false);
  };

  return (
    <Fade in={true}>
      <Box>
        <Box display="flex" justifyContent="flex-end" mb={2} pr={2} gap={1}>
          <Button variant="outlined" onClick={prepareChartImage} disabled={generating}>
            {generating ? 'Preparing Chart...' : 'Prepare document for PDF'}
          </Button>

          {chartImage && (
            <PDFDownloadLink
              document={<PdfExportDocument formData={formData} chartImage={chartImage} />}
              fileName="project-summary.pdf"
              style={{ textDecoration: 'none' }}
            >
              {({ loading }) =>
                loading ? (
                  <Button variant="outlined" disabled>
                    Generating PDF...
                  </Button>
                ) : (
                  <Button variant="contained">Download PDF</Button>
                )
              }
            </PDFDownloadLink>
          )}
        </Box>

        {/* On-screen content */}
        <Box sx={{ backgroundColor: '#fff', p: 2 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{
                  position: 'relative',
                  p: 3,
                  mb: 3,
                  backgroundColor: '#F9F9F9',
                  borderLeft: '4px solid #223A64',
                }}
              >
                <Box
                  component="img"
                  src={LogoImage}
                  alt="Logo"
                  sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    width: 200,
                    height: 'auto',
                  }}
                />

                <Grid container spacing={4} sx={{ mt: 4 }}>
                  <Grid item xs={12} md={6}>
                    <Box mb={2} display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Project Name:
                      </Typography>
                      <Typography variant="subtitle1">{formData?.projectInfo?.projectName || '—'}</Typography>
                    </Box>
                    <Box mb={2} display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Project Scope:
                      </Typography>
                      <Typography variant="subtitle1">{formData?.projectInfo?.projectScope || '—'}</Typography>
                    </Box>
                    <Box mb={2} display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Date:
                      </Typography>
                      <Typography variant="subtitle1">
                        {formData?.projectInfo?.date ? new Date(formData?.projectInfo?.date).toLocaleDateString() : '—'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box mb={2} display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Estimated By:
                      </Typography>
                      <Typography variant="subtitle1">{formData?.projectInfo?.estimatedBy || '—'}</Typography>
                    </Box>
                    <Box mb={2} display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Requested By:
                      </Typography>
                      <Typography variant="subtitle1">{formData?.projectInfo?.requestedBy || '—'}</Typography>
                    </Box>
                    <Box mb={2} display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Estimate Class:
                      </Typography>
                      <Typography variant="subtitle1">{formData?.projectInfo?.estimateClass || '—'}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <ProjectBudgetPieChart ref={chartRef} />
            </Grid>
          </Grid>

          <Grid container spacing={4} alignItems="flex-start">
            <Grid item xs={12} md={6}>
              <ProjectBudgetSummary />
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <ProjectMetricsSummary />
                </Grid>
                <Grid item xs={12}>
                  <CostAssumptionsSummary />
                </Grid>
                <Grid item xs={12}>
                  <ProjectQuantitiesSummary />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Fade>
  );
};

export default Summary;
